/**
 * BRE-5088 — recover a browser whose stored data is preventing the app from
 * loading.
 *
 * Cloudflare rejects a request whose headers exceed its budget with a 413,
 * before it reaches CloudFront, the WAF, or the Lambda. The `Cookie` header is
 * the part of that budget that grows over time and varies per browser, so a
 * user can reach a state where every Server Action is rejected and the app
 * cannot start — while a colleague in the same org is fine.
 *
 * The remedy is to clear the site's cookies. The user this was found through
 * was not technical enough to do that by hand, and telling a customer to open
 * devtools is not a fix — hence a button.
 *
 * ## What this can and cannot clear
 *
 * JavaScript can only expire cookies it can see. It CANNOT touch HttpOnly
 * cookies, which includes the two largest suspects:
 *
 *   - `wos-session` — cleared server-side by the `/logout` redirect this ends with.
 *   - `CF_Authorization` — Cloudflare Access's own cookie. Only Access can
 *     clear it (`/cdn-cgi/access/logout`), and it is only present on
 *     Access-protected origins. Deliberately not chained here: it would sign
 *     the user out of Access entirely on every recovery attempt, which is a
 *     heavier action than this button promises.
 *
 * So this is a strong partial reset — every third-party and app cookie plus
 * both web storages, then the session — not a guaranteed one.
 */

/** Expire a cookie across the scopes it could plausibly have been set on. */
export function expireCookie(name: string): void {
  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const hostname = window.location.hostname;

  // A cookie is only removable with a matching domain+path, and we cannot read
  // the attributes it was set with — so clear the host-only scope and the
  // registrable parent domain (where `.brev.io`-scoped cookies from sibling
  // subdomains live, which ride along on every request to this one).
  const labels = hostname.split(".");
  const parentDomain = labels.length > 2 ? `.${labels.slice(-2).join(".")}` : `.${hostname}`;

  document.cookie = `${name}=; ${expiry}; path=/`;
  document.cookie = `${name}=; ${expiry}; path=/; domain=${hostname}`;
  document.cookie = `${name}=; ${expiry}; path=/; domain=${parentDomain}`;
}

/**
 * Names of every cookie currently visible to JavaScript. Exported for testing —
 * the parsing is the part worth pinning, since a malformed jar is exactly the
 * situation this runs in.
 */
export function readVisibleCookieNames(cookieHeader: string): string[] {
  return cookieHeader
    .split(";")
    .map((entry) => entry.split("=")[0]?.trim() ?? "")
    .filter((name) => name.length > 0);
}

/**
 * Clear this site's client-side data, then sign out.
 *
 * Never throws — it runs from an error screen that is already the user's last
 * resort, so a failure in any one step must not prevent the others or block
 * the redirect.
 */
export function resetBrowserDataAndSignOut(): void {
  try {
    for (const name of readVisibleCookieNames(document.cookie)) {
      expireCookie(name);
    }
  } catch {
    // Keep going — the /logout redirect below is the load-bearing step.
  }

  try {
    localStorage.clear();
  } catch {
    // Storage can throw in private browsing / when disabled.
  }

  try {
    sessionStorage.clear();
  } catch {
    // As above.
  }

  // Hard navigation: /logout redirects cross-origin to WorkOS, which fails
  // CORS under the Next.js RSC router.
  window.location.href = "/logout";
}
